"use client";

import { Box, Grid, Skeleton } from "@mui/material";
import { FC, memo, useCallback, useEffect, useMemo } from "react";
import VehicleSuggestionCard from "@/components/Card/VehicleSuggestionCard";
import PaginationCustom from "@/components/Pagination/Pagination";
import VehicleSuggestionHorizontalCard from "@/components/Card/VehicleSuggestionHorizontalCard";
import Empty from "@/components/Empty/Empty";
import ButtonCustom from "@/components/Button/ButtonCustom";
import { useWantToBuyStore } from "@/contexts/useWantToBuy";
import type { VehicleSearchReponse } from "@/libs/types/vehicle.type";
import { useWishlist } from "@/contexts/useWishlist";
import { useFindMyCarStore } from "@/contexts/useFindMyCar";
import { useTranslation } from "@/i18n/client";
import color from "@/styles/color";
import { useSearchParams } from "next/navigation";
import { UsedCarFilter } from "@/libs/constant/key-localstorage.constant";
import { usePageLoadingStore } from "@/contexts/usePageLoading";
import WithLazy from "@/components/WithLazy";
import FindMyCarDrawer from "../find-my-car/FindMyCarDrawer";
import { setCookieFromServer } from "@/app/api/cookies/cookie";

type WantToBuyVehicleListProps = {
  data: VehicleSearchReponse | undefined;
};

const WantToBuyVehicleList: FC<WantToBuyVehicleListProps> = ({ data }) => {
  const limit = 15;
  const { t } = useTranslation();
  const { viewFilter, setViewFilter } = useWantToBuyStore();
  const { setVehicleByID, getVehiclesDict } = useWishlist();
  const { setShowFindMyCarDrawer } = useFindMyCarStore();
  const { setLoading, resetLoading, isLoading } = usePageLoadingStore();

  const vehicleDict = getVehiclesDict();
  const searchParams = useSearchParams();

  const gridSize = useMemo(() => {
    return viewFilter.viewMode === "vertical" ? 4 : 12;
  }, [viewFilter?.viewMode]);

  const { vehicleList, totalPage } = useMemo(() => {
    if (!data) return { vehicleList: [], totalPage: 0 };
    const totalPage = Math.ceil(data.TotalCount / limit);
    return { vehicleList: data.SearchResult, totalPage };
  }, [data]);

  const onChangePage = useCallback(
    (page: number) => {
      setViewFilter({ ...viewFilter, page });
      setLoading();
      const currentParams = new URLSearchParams(searchParams.toString());
      if (page !== 1) {
        currentParams.set("page", `${page}`);
      } else {
        currentParams.delete("page");
      }
      setCookieFromServer(UsedCarFilter, JSON.stringify({ ...viewFilter }), { httpOnly: true, secure: true });
    },
    [viewFilter, setViewFilter, searchParams, setLoading],
  );

  useEffect(() => {
    if (vehicleList) {
      resetLoading();
      fetch(`/api/cookies?cookie-name=${UsedCarFilter}`, {method: "DELETE"})
    }
  }, [vehicleList, resetLoading]);

  if (isLoading)
    return (
      <Grid
        container
        columnSpacing={{ xs: "8px", xl: "20px" }}
        rowSpacing={{
          xs: "16px",
          sm: "24px",
        }}
        columns={{
          xs: 8,
          md: 12,
          lg: 12,
          xl: 12,
        }}
      >
        {Array.from({ length: 9 }).map((_, index: number) => (
          <Grid item xs={4} key={`wtb-car-skeleton-${index + 1}`}>
            <Skeleton
              variant="rectangular"
              animation={"wave"}
              sx={{
                width: "100%",
                height: { xs: "700px !important", sm: "650px !important" },
                borderRadius: "16px",
              }}
            />
          </Grid>
        ))}
      </Grid>
    );
  if (vehicleList?.length === 0 || !vehicleList) {
    return (
      <Box
        sx={{
          background: color.white,
          borderRadius: "16px",
          width: "100%",
          height: "auto",
          paddingY: "80px",
        }}
      >
        <Empty
          chlid={
            <ButtonCustom
              label={t("FindMyCar.title")}
              onClick={() => {
                setShowFindMyCarDrawer(true);
              }}
              sx={{
                fontSize: { xs: "14px", xl: "16px" },
                fontWeight: 600,
                lineHeight: "24px",
                width: "181px",
              }}
            />
          }
        />
        <FindMyCarDrawer />
      </Box>
    );
  }

  return (
    <>
      <Grid
        container
        columnSpacing={{ xs: "8px", xl: "20px" }}
        rowSpacing={{
          xs: "16px",
          sm: "24px",
        }}
        columns={{
          xs: 8,
          md: 12,
          lg: 12,
          xl: 12,
        }}
      >
        {vehicleList?.map((item, index) => (
          <Grid
            item
            xs={gridSize}
            sm={gridSize}
            md={gridSize}
            lg={gridSize}
            xl={gridSize}
            key={`car-vertical-item-${index}`}
            sx={{
              ...(viewFilter.viewMode !== "vertical"
                ? {
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                  }
                : {}),
            }}
          >
            <WithLazy style={{ minHeight: 230 }} delay={0}>
              {viewFilter.viewMode === "vertical" ? (
                <VehicleSuggestionCard
                  data={item}
                  isWishlist={vehicleDict[item.VehicleMasterId]}
                  onClickWishlist={setVehicleByID}
                />
              ) : (
                <VehicleSuggestionHorizontalCard
                  data={item}
                  desktopSx={{}}
                  isWishlist={vehicleDict[item.VehicleMasterId]}
                  onClickWishlist={setVehicleByID}
                />
              )}
            </WithLazy>
          </Grid>
        ))}
      </Grid>
      <WithLazy style={{ minHeight: 100 }} delay={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: { xs: "24px" },
          }}
        >
          <PaginationCustom
            page={viewFilter?.page}
            defaultCount={totalPage}
            onChangePage={(page) => onChangePage(page)}
          />
        </Box>
      </WithLazy>
    </>
  );
};

export default memo(WantToBuyVehicleList);
